import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../components/AuthProvider';
import { getUserAccessibleClients } from '../lib/multiClientUtils';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import NextProspectingDaysCard from '../components/clientdashboard/NextProspectingDaysCard';
import PendingAppointmentsCard from '../components/clientdashboard/PendingAppointmentsCard';
import UpcomingAppointmentsSlider from '../components/clientdashboard/UpcomingAppointmentsSlider';
import ClientRdvConfirmationModal from '../components/clientdashboard/ClientRdvConfirmationModal';

interface NextProspectingDay {
  id: string;
  date: string;
  sdr_name: string;
}

interface Client {
  id: string;
  name: string;
  statut: string;
}

const ClientDashboardPage: React.FC = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [clientId, setClientId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [availableClients, setAvailableClients] = useState<Client[]>([]);
  const [selectedClientName, setSelectedClientName] = useState<string>('');

  const [nextProspectingDays, setNextProspectingDays] = useState<NextProspectingDay[]>([]);
  const [loadingNextProspecting, setLoadingNextProspecting] = useState(true);

  useEffect(() => {
    const fetchClientData = async () => {
      if (!profile) return;

      try {
        const clients = await getUserAccessibleClients(profile.user_id, profile.role);
        setAvailableClients(clients);

        if (clients.length > 0) {
          if (profile.role === 'client') {
            const { data, error } = await supabase
              .from('profile_clients')
              .select('client_id')
              .eq('profile_id', profile.id)
              .maybeSingle();

            if (error) {
              setClientId(clients[0].id);
              setSelectedClientName(clients[0].name);
            } else if (data) {
              setClientId(data.client_id);
              const client = clients.find(c => c.id === data.client_id);
              setSelectedClientName(client?.name || '');
            }
          } else {
            setClientId(clients[0].id);
            setSelectedClientName(clients[0].name);
          }
        }
      } catch (error) {
        console.error('Error fetching client data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchClientData();
  }, [profile]);

  const fetchNextProspectingDays = useCallback(async () => {
    if (!clientId) return;
    setLoadingNextProspecting(true);
    try {
      const today = new Date().toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('prospecting_schedule')
        .select(`id, date, profiles!sdr_id(sales_name)`)
        .eq('client_id', clientId)
        .gte('date', today)
        .order('date', { ascending: true })
        .limit(10);

      if (error) throw error;

      const formattedDays: NextProspectingDay[] = (data || []).map((item) => ({
        id: item.id,
        date: item.date,
        sdr_name: (item.profiles as any)?.sales_name || 'SDR non assigné',
      }));

      setNextProspectingDays(formattedDays);
    } catch (error) {
      console.error('Error fetching next prospecting days:', error);
    } finally {
      setLoadingNextProspecting(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (clientId) {
      setLoading(false);
      fetchNextProspectingDays();
    }
  }, [clientId, fetchNextProspectingDays]);

  const handleClientChange = (newClientId: string) => {
    const client = availableClients.find(c => c.id === newClientId);
    if (client) {
      setClientId(newClientId);
      setSelectedClientName(client.name);
    }
  };

  if (!profile || loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  const showClientSelector = profile.role === 'admin' || profile.role === 'super_admin';

  if (!clientId) {
    return (
      <div className="min-h-screen bg-white p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col items-center justify-center h-[60vh]">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Aucun client disponible</h2>
              <p className="text-gray-600">Veuillez contacter votre administrateur pour acceder aux donnees.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bonjour';
    if (hour < 18) return 'Bon apres-midi';
    return 'Bonsoir';
  };

  const displayName = profile?.full_name || profile?.sales_name || selectedClientName;

  return (
    <div className="min-h-screen bg-gray-50/50">
      {clientId && <ClientRdvConfirmationModal clientId={clientId} />}
      <div className="max-w-5xl mx-auto px-6 sm:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-1">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {getGreeting()}, {displayName}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Voici le resume de votre activite pour {selectedClientName}
              </p>
            </div>
            {showClientSelector && availableClients.length > 0 && (
              <select
                value={clientId || ''}
                onChange={(e) => handleClientChange(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                {availableClients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className="space-y-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <UpcomingAppointmentsSlider
              clientId={clientId}
              onNavigate={(id) => navigate(`/appointments/${id}`)}
            />

            <NextProspectingDaysCard
              days={nextProspectingDays}
              loading={loadingNextProspecting}
            />
          </div>

          <PendingAppointmentsCard
            clientId={clientId}
            onNavigate={(id) => navigate(`/appointments/${id}`)}
          />
        </div>
      </div>
    </div>
  );
};

export default ClientDashboardPage;
